// PRICED shell — the non-auto chase emails (AC cash, NA monthly Direct Debit):
// copy, then the market COMPARISON GRID of the chase's quotes (recommendation
// pinned top, rank badges, saving badge), then the PAYMENT OVERVIEW card
// with the "Call to renew" button. Logic ported verbatim from
// rensure-sme-smp/lib/messaging/layout.ts (pricedEmailHtml); markup in the HQ welcome-email design.
//
// Monthly policies compare on the INTEREST-BEARING monthly figure so the grid
// matches the card + TOBA exactly; cash policies compare on the annual total.

import type { BrandConfig } from "../brands";
import type { PaymentOverview, QuoteRow } from "../types";
import { escapeHtml, formatMoney, initialOf, payHref, round2, telHref } from "../format";
import {
  type BrandedEmailArgs,
  type Fact,
  art,
  bodyRow,
  button,
  emailDocument,
  notRenewingHtml,
  policyCardHtml,
  row,
  sectionLabel,
  table,
} from "./chrome";

export type PricedEmailArgs = BrandedEmailArgs & {
  quotes: QuoteRow[];
  payment: PaymentOverview;
  /** Policy reference — adds the "Pay online" button under the call CTA. */
  policyRef?: string;
  /** Which chase on that policy reference — the pay link's `?version=`. */
  versionRef?: string;
};

/** Fallback when a chase has no priced payment yet. */
export const NO_PAYMENT: PaymentOverview = {
  paymentType: "CASH",
  instalments: null,
  monthlyAmount: null,
  totalPayable: 0,
};

/** Podium position badge (replaces the SMP medal emoji). */
const rankBadge = (brand: BrandConfig, i: number, selected: boolean): string =>
  i < 3
    ? `<span class="rank" style="display:inline-block;min-width:18px;margin:0 7px 0 0;padding:1px 0;border-radius:999px;background:${selected ? brand.primary : brand.soft};color:${selected ? "#ffffff" : brand.primary};font-size:11px;line-height:16px;font-weight:700;text-align:center;vertical-align:2px;">${i + 1}</span>`
    : "";

/** The market-comparison grid: one card per quote, recommendation pinned top. */
function comparisonGridHtml(quotes: QuoteRow[], brand: BrandConfig, payment: PaymentOverview): string {
  const primary = brand.primary;
  // Recommendation first, then the rest cheapest-first.
  const ordered = [...quotes].sort((a, b) => {
    if (a.selected !== b.selected) return a.selected ? -1 : 1;
    return a.total - b.total;
  });

  const best = ordered[0];
  const maxTotal = Math.max(...ordered.map((q) => q.total));
  const topSaving = maxTotal - (best?.total ?? 0);

  const months = payment.instalments ?? 0;
  const monthly = payment.paymentType === "INSTALMENTS" && months > 0;
  const bestTotal = best?.total ?? 0;
  // Interest factor implied by the total payable vs the selected total, applied
  // to every insurer so the comparison is apples-to-apples.
  const factor = monthly && bestTotal > 0 ? payment.totalPayable / bestTotal : 1;
  const monthlyOf = (total: number) => round2((total * factor) / months);
  const shown = (total: number) => formatMoney(monthly ? monthlyOf(total) : total);
  const unitSub = monthly
    ? `per month · ${months} instalment${months === 1 ? "" : "s"}`
    : "per year";
  const lowestLabel = monthly ? "Lowest monthly premium" : "Lowest annual premium";
  const perUnit = monthly ? "/mo" : "/yr";
  const period = monthly ? "a month" : "a year";
  const displaySaving = monthly ? round2((topSaving * factor) / months) : topSaving;

  const cards = ordered
    .map((q, i) => {
      const insurer = escapeHtml(q.insurerName?.trim() || "Selected insurer");
      const initial = initialOf(q.insurerName?.trim() || "•");
      // The selected row shows the exact monthly the card + TOBA use.
      const amount = escapeHtml(
        q.selected && monthly && payment.monthlyAmount != null
          ? formatMoney(payment.monthlyAmount)
          : shown(q.total),
      );
      const rank = rankBadge(brand, i, q.selected);

      if (q.selected) {
        const saveBadge =
          displaySaving > 0
            ? `<span style="display:inline-block;margin-top:8px;background:#e1f3e9;color:#28624b;font-size:11px;line-height:16px;font-weight:700;padding:3px 9px;border-radius:999px;">Save ${escapeHtml(formatMoney(displaySaving))}${perUnit}</span>`
            : "";
        return table(
          `<tr><td class="quote-pad" style="padding:18px;">${table(`<tr>
                    <td class="quote-mark" width="58" valign="middle" style="padding:0 14px 0 0;">${table(`<tr><td width="44" height="44" align="center" valign="middle" style="width:44px;height:44px;background:${primary};border-radius:22px;color:#ffffff;font-size:18px;font-weight:700;text-align:center;line-height:44px;">${initial}</td></tr>`, "width:44px;")}</td>
                    <td valign="middle">
                      <span style="display:inline-block;background:${primary};color:#ffffff;font-size:10px;line-height:15px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;padding:3px 9px;border-radius:999px;margin:0 0 7px;">Recommended · Best price</span>
                      <span style="display:block;font-size:17px;line-height:23px;font-weight:700;color:${brand.ink};">${rank}${insurer}</span>
                      <span style="display:block;font-size:12px;line-height:18px;color:${brand.text};margin-top:2px;">${lowestLabel}</span>
                    </td>
                    <td align="right" valign="middle" style="white-space:nowrap;padding-left:10px;">
                      <span class="quote-price" style="display:block;font-size:24px;line-height:28px;letter-spacing:-0.6px;font-weight:700;color:${primary};">${amount}</span>
                      <span style="display:block;font-size:11px;line-height:16px;color:${brand.text};margin-top:2px;">${unitSub}</span>
                      ${saveBadge}
                    </td>
                  </tr>`)}</td></tr>`,
          `margin:0 0 10px;background:${brand.soft};border:2px solid ${primary};border-radius:14px;`,
        );
      }

      const rawDelta = q.total - (best?.total ?? q.total);
      const deltaShown = monthly ? round2((rawDelta * factor) / months) : rawDelta;
      const deltaLine =
        deltaShown > 0
          ? `<span style="display:block;font-size:11px;line-height:16px;color:${brand.text};margin-top:2px;">+${escapeHtml(formatMoney(deltaShown))} more</span>`
          : "";
      return table(
        `<tr><td class="quote-pad" style="padding:14px 18px;">${table(`<tr>
                    <td class="quote-mark" width="54" valign="middle" style="padding:0 14px 0 0;">${table(`<tr><td width="40" height="40" align="center" valign="middle" style="width:40px;height:40px;background:${brand.soft};border-radius:20px;color:${primary};font-size:16px;font-weight:700;text-align:center;line-height:40px;">${initial}</td></tr>`, "width:40px;")}</td>
                    <td valign="middle">
                      <span style="display:block;font-size:15px;line-height:21px;font-weight:700;color:${brand.ink};">${rank}${insurer}</span>
                      <span style="display:block;font-size:12px;line-height:18px;color:${brand.text};margin-top:2px;">${unitSub}</span>
                    </td>
                    <td align="right" valign="middle" style="white-space:nowrap;padding-left:10px;">
                      <span class="quote-price" style="display:block;font-size:17px;line-height:22px;font-weight:700;color:${brand.ink};">${amount}</span>
                      ${deltaLine}
                    </td>
                  </tr>`)}</td></tr>`,
        `margin:0 0 10px;background:#ffffff;border:1px solid ${brand.line};border-radius:14px;`,
      );
    })
    .join("\n");

  const intro =
    displaySaving > 0
      ? `You could save up to <strong style="color:${brand.ink};">${escapeHtml(formatMoney(displaySaving))}</strong> ${period} by renewing with our recommended insurer — here's how the market compares.`
      : `Here's how your renewal compares across insurers — we've highlighted the best price for you.`;

  return `${sectionLabel("We compared our panel of insurers", primary)}
              <p style="margin:0 0 16px;font-size:14px;line-height:22px;color:${brand.text};">
                ${intro}
              </p>
${cards}`;
}

/** The PAYMENT OVERVIEW card with the "Call to renew" button. */
function paymentCardHtml(
  payment: PaymentOverview,
  brand: BrandConfig,
  callNumber: string,
  policyRef?: string,
  versionRef?: string,
): string {
  const figure = (html: string) =>
    `<p class="pay-figure" style="margin:0;font-size:44px;line-height:52px;letter-spacing:-1.4px;font-weight:700;color:${brand.ink};">${html}</p>`;
  let lines: string;
  if (payment.paymentType === "INSTALMENTS") {
    const monthly = payment.monthlyAmount ?? 0;
    const months = payment.instalments ?? 0;
    lines = `${figure(`${escapeHtml(formatMoney(monthly))} <span style="font-size:15px;letter-spacing:0;font-weight:400;color:${brand.text};">/ month</span>`)}
                      <p style="margin:7px 0 0;font-size:14px;line-height:22px;color:${brand.text};">${months} monthly payment${months === 1 ? "" : "s"} of ${escapeHtml(formatMoney(monthly))}</p>`;
    // Per spec: monthly (instalment) policies do NOT show a 'total payable'.
  } else {
    lines = `<p style="margin:0 0 6px;font-size:11px;line-height:16px;letter-spacing:1.6px;font-weight:700;text-transform:uppercase;color:${brand.text};">Pay in full</p>
                      ${figure(escapeHtml(formatMoney(payment.totalPayable)))}`;
  }

  // Second CTA under the call button — pay the renewal online, no phone call.
  const payOnlineHtml = policyRef
    ? `<div style="height:10px;line-height:10px;font-size:1px;">&nbsp;</div>${button(brand, "Pay online", payHref(policyRef, versionRef), true)}`
    : "";

  return row(
    `${table(
      `<tr><td valign="top"><p style="margin:0 0 10px;font-size:11px;line-height:16px;letter-spacing:1.6px;font-weight:700;text-transform:uppercase;color:${brand.primary};">Your payment overview</p><h2 style="margin:0 0 12px;font-size:27px;line-height:32px;letter-spacing:-.7px;font-weight:700;color:${brand.ink};">Payment Overview</h2></td><td class="headline-art" width="91" valign="top" style="padding-left:12px;">${art(brand, "certificate.png", 79)}</td></tr>`,
    )}
              <!-- Payment overview -->
              <div style="padding:12px 0 0;">${lines}</div>
              <div style="height:22px;line-height:22px;font-size:1px;">&nbsp;</div>
              ${button(brand, `Call to renew — ${escapeHtml(callNumber)}`, telHref(callNumber))}
              ${payOnlineHtml}
              <p style="margin:12px 0 0;font-size:12px;line-height:18px;color:${brand.text};text-align:center;">Our UK-based team is ready to help on <a href="${telHref(callNumber)}" style="color:${brand.primary};text-decoration:none;font-weight:700;white-space:nowrap;">${escapeHtml(callNumber)}</a>.</p>`,
    `padding:29px 36px;background:${brand.soft};border-top:1px solid ${brand.line};border-bottom:1px solid ${brand.line};`,
    "pad",
  );
}

export function pricedEmailHtml(args: PricedEmailArgs): string {
  const { brand, data, bodyHtml, callNumber, quotes, payment, policyRef, versionRef } = args;
  const chosen = quotes.find((q) => q.selected)?.insurerName?.trim();
  const facts: Fact[] = [
    ...(chosen ? [["insurer", "Recommended insurer", escapeHtml(chosen)] as Fact] : []),
    ["policy", "Payment", payment.paymentType === "INSTALMENTS" ? "Monthly instalments" : "Pay in full"],
  ];

  return emailDocument({
    brand,
    eyebrow: "Your renewal",
    heading: "Your renewal is due",
    policyCard: policyCardHtml(brand, data, facts),
    marketing: false,
    iptNote: true,
    rows: `${bodyRow(brand, `${bodyHtml}\n${comparisonGridHtml(quotes, brand, payment)}`, "padding-bottom:24px;")}
${paymentCardHtml(payment, brand, callNumber, policyRef, versionRef)}
${row(notRenewingHtml(brand, callNumber), "padding:24px 36px 30px;", "pad")}`,
  });
}
